<template>
    <vx-data-table
        :columns="kioskTeamSsoLoginColumns"
        :rows="logins"
        rowKey="id"
        :loading="loading"
        :totalCount="totalCount"
        :hasMore="hasMore"
        :loadingMore="loadingMore"
        toolbar
        searchPlaceholder="Email"
        :filterFields="kioskTeamSsoLoginFilterFields"
        :search="search"
        :filters="filters"
        v-model:sort="sort"
        v-model:density="density"
        v-model:visibleColumns="visibleColumns"
        @update:search="updateSearch"
        @update:filters="updateFilters"
        @loadMore="loadMore"
    >
        <template v-slot:cell-created_at="{ row }">
            {{ shortDateTime(row.created_at) }}
        </template>

        <template v-slot:cell-user_id="{ row }">
            <a
                v-if="row.user_id !== null"
                data-test="user-link"
                :href="`/kiosk/users/${row.user_id}`"
                class="min-w-0 truncate font-bold text-slate-800!"
            >
                {{ row.user_name ?? row.user_id }}
            </a>
            <span v-else class="text-slate-500">-</span>
        </template>

        <template v-slot:cell-result="{ row }">
            <vx-badge size="xs" :color="row.result === 'success' ? 'success' : 'rose'" data-test="result">
                {{ row.result === 'success' ? 'Success' : 'Failed' }}
            </vx-badge>
        </template>

        <template v-slot:cell-method="{ row }">
            {{ methodLabel(row.method) }}
        </template>

        <template v-slot:cell-failure_reason="{ row }">
            <vx-popover v-if="row.failure_reason" hover teleport :arrow="true" placement="top">
                <span data-test="failure-reason">{{ failureReasonLabel(row.failure_reason) }}</span>
                <template v-slot:content>
                    <code class="text-xs">{{ row.failure_reason }}</code>
                </template>
            </vx-popover>
            <span v-else class="text-slate-500">-</span>
        </template>

        <template v-slot:cell-ip_address="{ row }">
            <span v-if="row.ip_address">{{ row.ip_address }}</span>
            <span v-else class="text-slate-500">-</span>
        </template>

        <template v-slot:cell-user_agent="{ row }">
            <vx-popover v-if="row.user_agent" hover teleport :arrow="true" placement="top" class="w-full">
                <button
                    type="button"
                    class="block max-w-full text-left focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                    data-test="user-agent"
                >
                    <span class="block truncate">{{ userAgentLabel(row.user_agent) }}</span>
                </button>
                <template v-slot:content>
                    <div class="max-w-xs break-all">{{ row.user_agent }}</div>
                </template>
            </vx-popover>
            <span v-else class="text-slate-500">-</span>
        </template>

        <template v-slot:empty>
            <div v-if="narrowed" class="flex flex-col items-center gap-6 text-xl text-slate-500">
                Nothing matches these filters
                <vx-button data-test="clear-filters" color="muted-dark" @click="clearFilters"
                    >Clear filters

                    <font-awesome-icon widthAuto :icon="faCircleX" />
                </vx-button>
            </div>
            <div v-else class="flex flex-col items-center gap-6 text-xl text-slate-500">No login attempts yet</div>
        </template>
    </vx-data-table>
</template>

<script lang="ts" setup>
import { faCircleX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxButton, VxDataTable, VxPopover, type VxDataTableDensity } from '@voxie/frontend-components';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import { isCancel } from 'axios';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { adminSsoClient } from '~/services';
import type { KioskTeamSsoLogin } from '~/services/adminSsoClient.types';
import { shortDateTime } from '~/utils/date';
import debounce from '~/utils/debounce';
import { isEqual } from '~/utils/helpers';
import { userAgentLabel } from '~/utils/userAgent';
import {
    KIOSK_TEAM_SSO_LOGINS_MAX_FILTER_VALUES,
    columnsStorageKey,
    defaultSort,
    densityStorageKey,
    exceedsFilterValueLimit,
    failureReasonLabel,
    isNarrowed,
    kioskTeamSsoLoginColumns,
    kioskTeamSsoLoginFilterFields,
    methodLabel,
    nextCursorFrom,
    toLoginsCountParams,
    toLoginsParams,
    toSearchQuery,
    type KioskTeamSsoLoginsFilter,
    type KioskTeamSsoLoginsSort,
} from './kioskTeamSsoLoginsTableHelpers';

const props = defineProps<{
    teamId: number;
}>();

const toasted = useToasted();

const loginsCancelKey = Symbol('kiosk-team-sso-logins');
const countCancelKey = Symbol('kiosk-team-sso-logins-count');

const logins = ref<KioskTeamSsoLogin[]>([]);
const nextCursor = ref<string | null>(null);
const totalCount = ref<number | null>(null);
const loading = ref(true);
const loadingMore = ref(false);

const search = ref('');
const query = ref('');
const sort = ref<KioskTeamSsoLoginsSort>(defaultSort);
const filters = ref<KioskTeamSsoLoginsFilter[]>([]);

const visibleColumns = useLocalStorage<string[] | null>(() => columnsStorageKey(props.teamId), null, {
    serializer: StorageSerializers.object,
});
const density = useLocalStorage<VxDataTableDensity>(() => densityStorageKey(props.teamId), 'comfortable', {
    writeDefaults: false,
});

const countParams = computed(() => toLoginsCountParams(query.value, filters.value));
const listParams = computed(() => toLoginsParams(query.value, sort.value, filters.value));
const narrowed = computed(() => isNarrowed(countParams.value));
const hasMore = computed(() => nextCursor.value !== null);

const loadLogins = async () => {
    loading.value = true;
    loadingMore.value = false;
    nextCursor.value = null;

    try {
        const { data } = await adminSsoClient.getTeamSsoLogins(props.teamId, listParams.value, {
            cancelKey: loginsCancelKey,
        });

        logins.value = data.data;
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        logins.value = [];
        totalCount.value = null;
        loading.value = false;
        toasted.global.platform_error();
    }
};

const loadMore = async () => {
    loadingMore.value = true;

    try {
        const { data } = await adminSsoClient.getTeamSsoLogins(
            props.teamId,
            { ...listParams.value, cursor: nextCursor.value },
            { cancelKey: loginsCancelKey }
        );

        logins.value = [...logins.value, ...data.data];
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loadingMore.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        loadingMore.value = false;
        toasted.global.platform_error();
    }
};

const loadCount = async () => {
    try {
        const { data } = await adminSsoClient.getTeamSsoLoginsCount(props.teamId, countParams.value, {
            cancelKey: countCancelKey,
        });

        totalCount.value = data.count;
    } catch (error) {
        if (!isCancel(error)) {
            totalCount.value = null;
        }
    }
};

const applySearch = debounce((value: string) => {
    query.value = toSearchQuery(value);
});

const updateSearch = (value: string) => {
    search.value = value;
    applySearch(value);
};

const updateFilters = (next: KioskTeamSsoLoginsFilter[]) => {
    if (exceedsFilterValueLimit(next)) {
        toasted.error(`Select up to ${KIOSK_TEAM_SSO_LOGINS_MAX_FILTER_VALUES} values per filter.`);
        return;
    }

    filters.value = next;
};

const clearFilters = () => {
    applySearch.cancel();
    search.value = '';
    query.value = '';
    filters.value = [];
};

watch(listParams, (next, previous) => {
    if (!isEqual(next, previous)) {
        loadLogins();
    }
});

watch(countParams, (next, previous) => {
    if (!isEqual(next, previous)) {
        loadCount();
    }
});

onMounted(() => {
    loadLogins();
    loadCount();
});

onBeforeUnmount(() => {
    applySearch.cancel();
});
</script>
